/**
 * TimelineBlock - Display events on a timeline
 */

import React from 'react'
import { TimelineBlock as TimelineBlockType } from '../../../../lib/cockpit/schemas'

interface TimelineBlockProps {
  data: TimelineBlockType['data']
  blockId: string
  compact?: boolean
}

const IMPORTANCE_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-gray-400',
}

function getEventSourceLinks(event: any): Array<{ url: string; title: string }> {
  const anchors = Array.isArray(event?.evidence_anchors)
    ? event.evidence_anchors
    : Array.isArray(event?.anchors)
      ? event.anchors
      : []

  const seenUrls = new Set<string>()
  const resolved: Array<{ url: string; title: string }> = []

  anchors.forEach((anchor: any, index: number) => {
    const sourceId = typeof anchor?.source_id === 'string' ? anchor.source_id : ''
    if (sourceId.startsWith('FALLBACK_') || sourceId.startsWith('AUTO_') || sourceId.startsWith('timeline_')) {
      return
    }

    const url =
      (typeof anchor?.source_url === 'string' && anchor.source_url) ||
      (typeof sourceId === 'string' && sourceId.startsWith('http') ? sourceId : '')

    if (!url || seenUrls.has(url)) return

    seenUrls.add(url)
    resolved.push({
      url,
      title: anchor?.locator || anchor?.snippet || `Source ${index + 1}`,
    })
  })

  return resolved
}

/** Strip raw "impact score" text from description so we show importance as badge only */
function cleanEventDescription(desc: string | undefined): string | undefined {
  if (!desc || typeof desc !== 'string') return desc
  return desc
    .replace(/\bimpact\s*score\s*[:=]\s*\d+(\.\d+)?/gi, '')
    .replace(/\bimportance\s*[:=]\s*(high|medium|low)/gi, '')
    .trim()
    .replace(/\n{2,}/g, '\n') || undefined
}

function TimelineBlock({ data, blockId, compact }: TimelineBlockProps) {
  const events = data.events || []
  const sortedEvents = [...events].sort((a, b) =>
    new Date((a as any).date || 0).getTime() - new Date((b as any).date || 0).getTime()
  )
  const citedEvents = sortedEvents.filter((event) => getEventSourceLinks(event).length > 0)

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const eventId = (e: any) => e.event_id || e.id || ''
  const eventDate = (e: any) => e.date || ''

  if (compact) {
    return (
      <div className="space-y-2">
        {citedEvents.slice(0, 5).map((event, i) => (
          <div key={eventId(event) || i} className="flex items-center gap-3 text-sm">
            <span className="text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
              {formatDate(eventDate(event))}
            </span>
            <span className="text-gray-800 dark:text-gray-200">{(event as any).title}</span>
          </div>
        ))}
        {citedEvents.length === 0 && (
          <div className="text-sm text-gray-500">No cited events available</div>
        )}
        {citedEvents.length > 5 && (
          <div className="text-sm text-gray-500">+{citedEvents.length - 5} more</div>
        )}
      </div>
    )
  }

  return (
    <div className="relative max-w-4xl">
      {/* Timeline line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
      
      <div className="space-y-6">
        {citedEvents.map((event, index) => {
          const e = event as any
          const desc = cleanEventDescription(e.description)
          const sourceLinks = getEventSourceLinks(e)
          return (
            <div key={eventId(e) || index} className="relative pl-10">
              {/* Dot */}
              <div className={`
                absolute left-2.5 w-3 h-3 rounded-full
                ${e.importance && e.importance in IMPORTANCE_COLORS ? IMPORTANCE_COLORS[e.importance as keyof typeof IMPORTANCE_COLORS] : 'bg-blue-500'}
                ring-4 ring-white dark:ring-gray-900
              `} />
              
              {/* Content */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {e.title}
                    </h4>
                    {desc && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {desc}
                      </p>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                    {formatDate(eventDate(e))}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {e.category && (
                    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {e.category}
                    </span>
                  )}
                  {e.importance && (
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                      e.importance === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                      e.importance === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {e.importance}
                    </span>
                  )}
                </div>
                {sourceLinks.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                      Sources
                    </span>
                    {sourceLinks.slice(0, 3).map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={source.title}
                        className="inline-flex max-w-[220px] items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:border-blue-200 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
                      >
                        <span className="truncate">{source.title}</span>
                        <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ))}
                    {sourceLinks.length > 3 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        +{sourceLinks.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {citedEvents.length === 0 && (
          <div className="pl-10 text-sm text-gray-500 dark:text-gray-400">
            No cited events available.
          </div>
        )}
      </div>
    </div>
  )
}

export default TimelineBlock



